/**
 * CorrespondentOnboardingTab — the "Correspondent / BOP / Broker Onboarding
 * Checklist" (RF0308102026.1.0) as a live form, one per correspondent.
 *
 * Contract: eastview-os frontend_documentation/correspondent-onboarding-checklist.md §3.
 *   GET  /correspondents/{id}/onboarding_checklist
 *        → { checklist, template[], progress, auditTrail[], options }
 *   POST /correspondents/{id}/onboarding_checklist   (PATCH: only what changed,
 *        plus an optional `note` stamped on every audit entry of that save)
 *
 * Two save paths, on purpose:
 * - The engagement / review / execution / go-live FIELDS are one draft saved by
 *   the floating PendingChangesBar, the same way the Profile tab saves.
 * - Each post-execution DOCUMENT ITEM is its own record: the pinned edit button
 *   opens a small dialog that saves that one item immediately (with an optional
 *   history note), because the server stamps who / when on it at that moment.
 *   An item save never touches the unsaved field draft.
 *
 * Every select reads the `options` the GET returns — they are matched by string
 * equality server-side, so nothing is retyped here beyond a fallback.
 *
 * Laid out with the Profile tab's primitives (`PricingPageLayout` +
 * `ProfileSection` + the 12-column `Grid`) so the tabs read as one system.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import PropTypes from "prop-types";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import AddLinkRounded from "@mui/icons-material/AddLinkRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import RadioButtonCheckedRounded from "@mui/icons-material/RadioButtonCheckedRounded";
import CheckBoxRounded from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRounded from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../components/layout/Flx";
import PendingChangesBar from "../../../components/common/PendingChangesBar";
import QuickEditDialog from "../../../components/forms/QuickEditDialog";
import {
  FormDateField,
  FormFieldGrid,
  FormSelectField,
  FormTextField,
} from "../../../components/forms/FormPrimitives";
import {
  ONBOARDING_BLANK_FORMS_URL,
  docId,
  oidOf,
  useCorrespondentsApi,
} from "../../../hooks/useCorrespondentsHook";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { slate, status as statusColors, teal } from "../../../theme/colors";
import { useEnv } from "../../../hooks/useEnv";
import { segmentedGroupSx } from "../../../theme/activeStates";
import { FIELD_TEXT, FORM, formSx, px } from "../../../theme/formStyles";
import {
  PricingFieldText as PricingField,
  PricingPageLayout,
  T as PT,
} from "../../admin/AdminPricingScreen/pricingUi";
import { ActionCell, actionColumn } from "../../admin/shared/adminTableActions";
import AgreementDocumentModal from "./AgreementDocumentModal";
import { FileSlot } from "./CorrespondentFileSlot";
import { CorrespondentUploadPanel } from "./CorrespondentPanelForms";
import {
  Cell,
  ColumnHead,
  FIELD_SX,
  Grid,
  ProfileSection,
  SectionBadge,
} from "./correspondentForm";
import { SoftBadge } from "./correspondentUi";

/* ───────────────────────────── constants ───────────────────────────── */

// Fallbacks only — verbatim from the ONBOARDING_* option lists in eastview-os
// app/models/correspondents.py, used until the GET's `options` arrive.
const FALLBACK_OPTIONS = {
  channel: ["Eastview", "Lendmarq"],
  clientType: ["Correspondent", "BOP", "Broker"],
  agreementTypes: ["MLPA", "LFA", "Broker Agreement"],
  addedToTracker: ["Yes", "Pending"],
  itemStatus: ["Pending", "Received", "Completed", "N/A"],
};

// Which agreement type brings each document section in (ONBOARDING_SECTION_AGREEMENTS).
const SECTIONS = [
  {
    key: "post_close",
    title: "Post-Close (MLPA)",
    short: "Post-Close",
    agreements: ["MLPA"],
  },
  {
    key: "pre_close",
    title: "Pre-Close (LFA / Broker)",
    short: "Pre-Close",
    agreements: ["LFA", "Broker Agreement"],
  },
];
const SECTION_SHORT = Object.fromEntries(SECTIONS.map((s) => [s.key, s.short]));

// The rail's items; each gets its section's status (check / ring) in the tab.
const RAIL = [
  { id: "onb-required", label: "Required documents" },
  { id: "onb-engagement", label: "Initial engagement" },
  { id: "onb-questionnaire", label: "Prospect questionnaire" },
  { id: "onb-review", label: "Senior & legal review" },
  { id: "onb-execution", label: "Compliance" },
  { id: "onb-documents", label: "Post-execution docs" },
  { id: "onb-golive", label: "Notification & go-live" },
  { id: "onb-history", label: "History" },
];

// The non-item checklist fields this tab edits, and how each is held in the
// draft: "date" → yyyy-MM-dd, "bool" → "Yes" / "No" / "", "list" → array,
// "toggle" → boolean, anything else → string.
const FIELDS = {
  channel: "text",
  clientType: "text",
  ndaDrafted: "bool",
  ndaSentDate: "date",
  questionnaireSentDate: "date",
  questionnaireReceivedDate: "date",
  reviewedBy: "text",
  reviewDate: "date",
  agreementTypes: "list",
  agreementSentDate: "date",
  agreementExecutedDate: "date",
  amaApplicable: "toggle",
  amaSentDate: "date",
  amaExecutedDate: "date",
  notificationEmailSentDate: "date",
  addedToTracker: "text",
  relationshipManager: "text",
  relationshipManagerSignOff: "bool",
  relationshipManagerSignOffDate: "date",
  finalSignedOffBy: "text",
  finalSignOffDate: "date",
};

// History labels for the top-level fields (audit `field` values).
const FIELD_LABELS = {
  channel: "Channel",
  clientType: "Client type",
  ndaDrafted: "NDA drafted",
  ndaSentDate: "NDA sent",
  questionnaireSentDate: "Questionnaire sent",
  questionnaireReceivedDate: "Questionnaire received",
  reviewedBy: "Reviewed by",
  reviewDate: "Review date",
  agreementTypes: "Agreement types",
  agreementSentDate: "Agreement sent",
  agreementExecutedDate: "Agreement executed",
  amaApplicable: "AMA applies",
  amaSentDate: "AMA sent",
  amaExecutedDate: "AMA executed",
  notificationEmailSentDate: "Notification email sent",
  addedToTracker: "Added to tracker",
  relationshipManager: "Relationship manager",
  relationshipManagerSignOff: "RM sign-off",
  relationshipManagerSignOffDate: "RM sign-off date",
  finalSignedOffBy: "Final signed off by",
  finalSignOffDate: "Final sign-off date",
};

const ITEM_ATTR_LABELS = {
  status: "status",
  statusDate: "date",
  statusBy: "marked by",
  documentIds: "linked documents",
  value: "value",
  notes: "notes",
};

// The warehouse-lender line asks for the lender's NAME as well as a status.
// The statuses that carry a received / completed date.
const DATED_STATUSES = ["Received", "Completed"];

const VALUE_ITEMS = { "post_close.warehouse_lender": "Warehouse lender" };

// The document category an upload from an item starts on (it can be changed in
// the upload panel). Items with no matching category start on "Other".
const ITEM_UPLOAD_CATEGORY = {
  w9: "W9",
  formation_documents: "Corporate Docs",
  operating_agreement: "Corporate Docs",
  ownership_breakdown: "Corporate Docs",
  structure_chart: "Corporate Docs",
  eo_policy: "Insurance",
  background_checks: "Background Authorization",
};

/* ───────────────────────────── helpers ───────────────────────────── */

const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");
const boolToYesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "");
const yesNoToBool = (v) => (v === "Yes" ? true : v === "No" ? false : null);
const isDateish = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}(T|$)/.test(v);

// A value the user picked as a DATE comes back as midnight; a server stamp
// carries a real clock. Read the first by its calendar fields (so it can't slip
// a day in Eastern) and the second as the Eastern date of that moment.
const fmtWhen = (v) => {
  if (!v) return "";
  return /T00:00:00(\.0+)?(Z|\+00:00)?$/.test(String(v)) || !String(v).includes("T")
    ? formatDate(v, "", "MMM d, yyyy")
    : formatDateTime(v, "date");
};

const buildDraft = (checklist) => {
  const c = checklist || {};
  const out = {};
  Object.entries(FIELDS).forEach(([k, kind]) => {
    const v = c[k];
    if (kind === "date") out[k] = toDateInput(v);
    else if (kind === "bool") out[k] = boolToYesNo(v);
    else if (kind === "list") out[k] = Array.isArray(v) ? [...v] : [];
    else if (kind === "toggle") out[k] = v === true;
    else out[k] = v ?? "";
  });
  return out;
};

// Draft value → what the POST wants. Empty clears (`null`), never `""` (which
// the backend reads as "not provided" and ignores).
const toWire = (kind, v) => {
  if (kind === "date") return v || null;
  if (kind === "bool") return yesNoToBool(v);
  if (kind === "list") return v;
  if (kind === "toggle") return !!v;
  return v === "" ? null : v;
};

const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const errorMessage = (e, fallback) =>
  e?.response?.data?.detail || e?.message || fallback;

/** Which document sections the chosen agreement types bring in. */
const visibleSectionKeys = (agreementTypes) => {
  const types = agreementTypes || [];
  if (!types.length) return SECTIONS.map((s) => s.key);
  return SECTIONS.filter((s) => s.agreements.some((a) => types.includes(a))).map(
    (s) => s.key,
  );
};

const isItemDone = (tpl, item) =>
  !!item?.status && (item.status === tpl.doneStatus || item.status === "N/A");

/* ───────────────────────────── small pieces ───────────────────────────── */

const ReadOnlyValue = ({ children }) => (
  <Box
    sx={{
      minHeight: 40,
      display: "flex",
      alignItems: "center",
      px: 1.75,
      borderRadius: "6px",
      border: `1px solid ${PT.line}`,
      background: PT.surface2,
      fontSize: "13px",
      color: PT.ink2,
      overflowWrap: "anywhere",
    }}
  >
    {children || "—"}
  </Box>
);

const SelectInput = ({ value, onChange, options, placeholder = "— Not set —" }) => (
  <TextField
    select
    size="small"
    fullWidth
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    sx={FIELD_SX}
  >
    <MenuItem value="" sx={{ fontSize: 13, color: PT.faint }}>
      {placeholder}
    </MenuItem>
    {options.map((o) => (
      <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
        {o}
      </MenuItem>
    ))}
  </TextField>
);

// PARTNER DIVERGENCE (see CLAUDE.md → Correspondent Onboarding Checklist):
// the Eastview build only onboards Eastview-channel counterparties, so Channel
// is fixed and Client type offers just the two Eastview client types. A saved
// BOP (Lendmarq channel) still shows, so the record isn't silently changed.
// Every other partner build (Lendmarq) keeps the full Channel / Client type
// pickers from the backend's options.
const EASTVIEW_CHANNEL = "Eastview";
const EASTVIEW_CLIENT_TYPES = ["Correspondent", "Broker"];
// Toggle value for "no client type" — saved as null.
const NO_CLIENT_TYPE = "__none__";

// Today as yyyy-MM-dd on the Eastern calendar — what a ticked box stamps.
const todayInput = () => formatDateTime(new Date().toISOString(), "yyyy-MM-dd");

/**
 * A card body with a side panel: the main fields on the left, a hairline, and
 * a narrow panel on the right (NDA, Asset Management Agreement). Stacks on
 * small screens, where the hairline drops out.
 */
const WithSidePanel = ({ children, panel }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        md: "minmax(0, 1fr) 1px minmax(380px, 460px)",
      },
      gap: 3,
    }}
  >
    <Box sx={{ minWidth: 0 }}>{children}</Box>
    <Box sx={{ display: { xs: "none", md: "block" }, background: slate[200] }} />
    <Box sx={{ minWidth: 0 }}>{panel}</Box>
  </Box>
);

/** A field label with its one-line explanation right under it (above the control). */
const LabelWithHint = ({ label, hint }) => (
  <Box sx={{ mb: 1 }}>
    <Box sx={{ ...FIELD_TEXT.label, mb: 0 }}>{label}</Box>
    {hint ? (
      <Box sx={{ fontSize: 11.5, lineHeight: 1.45, color: PT.muted, mt: "2px" }}>
        {hint}
      </Box>
    ) : null}
  </Box>
);

/**
 * A bordered option with its own box (or circle, for an "all off" option) —
 * the check-box look in a chip, so several can be on at once. Selected = the
 * level-1 teal tint.
 */
const OptionChip = ({ label, checked, onClick, radio }) => {
  const Icon = radio
    ? checked
      ? RadioButtonCheckedRounded
      : RadioButtonUncheckedRounded
    : checked
      ? CheckBoxRounded
      : CheckBoxOutlineBlankRounded;
  return (
    <ButtonBase
      role={radio ? "radio" : "checkbox"}
      aria-checked={checked}
      onClick={onClick}
      sx={{
        gap: 0.75,
        height: 34,
        px: 1.25,
        borderRadius: "6px",
        border: `1px solid ${checked ? teal[600] : PT.line}`,
        bgcolor: checked ? teal[50] : "#fff",
        color: checked ? teal[700] : PT.ink,
        fontSize: 13,
        fontWeight: checked ? 600 : 500,
        transition: "border-color .13s, background-color .13s",
        "&:hover": { borderColor: checked ? teal[700] : slate[400] },
        "&:focus-visible": { outline: `2px solid ${teal[600]}`, outlineOffset: 1 },
      }}
    >
      <Icon sx={{ fontSize: 16, color: checked ? teal[600] : slate[400] }} />
      {label}
    </ButtonBase>
  );
};

// "Oct 23" from a yyyy-MM-dd draft value.
const shortDay = (v) => formatDate(v, "", "MMM d");

const DateInput = ({ value, onChange }) => (
  <TextField
    size="small"
    fullWidth
    type="date"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    sx={FIELD_SX}
  />
);

/**
 * Email picker over the portal's users. Free entry is allowed — these are email
 * fields, not an enum, and a legal reviewer may not be a portal user.
 */
const EmailInput = ({ value, onChange, users }) => {
  const options = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .filter((u) => u?.emailAddress)
        .map((u) => ({
          email: u.emailAddress,
          name: [u.firstName, u.lastName].filter(Boolean).join(" "),
        }))
        .sort((a, b) => a.email.localeCompare(b.email)),
    [users],
  );
  const nameOf = (email) => options.find((o) => o.email === email)?.name;
  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options.map((o) => o.email)}
      value={value || null}
      onChange={(_, next) => onChange(next || "")}
      onInputChange={(_, next, reason) => {
        if (reason === "input") onChange(next);
      }}
      renderOption={(props, email) => (
        <Box component="li" {...props} key={email} sx={{ fontSize: 13 }}>
          <Flx column>
            <span>{nameOf(email) || email}</span>
            {nameOf(email) && (
              <Box component="span" sx={{ fontSize: 11.5, color: PT.muted }}>
                {email}
              </Box>
            )}
          </Flx>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder="name@company.com" sx={FIELD_SX} />
      )}
    />
  );
};

const DocLink = ({ doc, onOpen }) => (
  <Box
    component="button"
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onOpen(doc);
    }}
    sx={{
      border: "none",
      background: "none",
      p: 0,
      cursor: "pointer",
      textAlign: "left",
      fontSize: "12.5px",
      fontWeight: 600,
      color: PT.accent,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "100%",
      "&:hover": { textDecoration: "underline" },
    }}
  >
    {doc.file_display_name || doc.file_name_as_loaded || "Untitled"}
  </Box>
);

const ITEM_STATUS_TONE = {
  done: "green",
  "N/A": "slate",
  Pending: "amber",
  other: "steel",
};

/* ───────────────────────────── item dialog ───────────────────────────── */

function ItemEditDialog({
  open,
  editing,
  statusOptions,
  documents,
  saving,
  error,
  onChange,
  onClose,
  onSave,
  onUpload,
}) {
  const tpl = editing?.tpl;
  const form = editing?.form || {};
  const valueLabel = tpl ? VALUE_ITEMS[tpl.id] : null;
  const docOptions = useMemo(
    () =>
      (documents || []).map((d) => ({
        value: oidOf(d),
        label: `${d.file_display_name || d.file_name_as_loaded || "Untitled"}${
          d.category ? ` · ${d.category}` : ""
        }`,
      })),
    [documents],
  );
  const pickedDocs = docOptions.filter((o) =>
    (form.documentIds || []).includes(o.value),
  );
  // Only a received / completed item carries a date; Pending and N/A don't.
  const showDate = DATED_STATUSES.includes(form.status);

  return (
    <QuickEditDialog
      open={open}
      title={tpl ? `${tpl.label} · ${SECTION_SHORT[tpl.section]}` : ""}
      description={
        tpl
          ? `Done when ${tpl.doneStatus}${
              tpl.required ? "" : " · optional, not counted in progress"
            }. N/A is allowed on any line.`
          : undefined
      }
      onClose={onClose}
      onSave={onSave}
      saving={saving}
      saveText="Save item"
      hideFieldLabel={false}
      enterHint={false}
      width={560}
      bodySx={{ pt: px(FORM.s.s5) }}
      fieldText="field"
      status={
        error ? (
          <Box component="span" sx={{ color: statusColors.error.fg }}>
            {error}
          </Box>
        ) : undefined
      }
    >
      <FormFieldGrid>
        <FormSelectField
          span={showDate ? 3 : 6}
          label="Status"
          value={form.status || "Pending"}
          options={statusOptions}
          onChange={(v) => onChange({ status: v || "Pending" })}
        />
        {showDate && (
          <FormDateField
            span={3}
            label="Status date"
            value={form.statusDate}
            onChange={(v) => onChange({ statusDate: v })}
            helperText="Leave as is to stamp today; set it only to backdate."
          />
        )}
        {valueLabel && (
          <FormTextField
            span={6}
            label={valueLabel}
            value={form.value}
            onChange={(v) => onChange({ value: v })}
          />
        )}
        <Box sx={{ gridColumn: "span 6", minWidth: 0 }}>
          <PricingField
            label="Linked documents"
            helperText="Pick from this correspondent's documents, or upload a new one."
          >
            {/* Upload from inside the form — the dashed note-row recipe
                (FORM_STYLE_GUIDE.md §0, as on the Experience dialog). */}
            <Box
              component="button"
              type="button"
              onClick={onUpload}
              disabled={saving}
              sx={{
                ...formSx.note,
                mb: px(FORM.s.s3),
                display: "flex",
                alignItems: "center",
                gap: px(FORM.s.s3),
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": {
                  bgcolor: FORM.color.surface3,
                  borderColor: FORM.color.ink4,
                },
                "&:disabled": { cursor: "default", opacity: 0.6 },
              }}
            >
              <UploadFileOutlined sx={{ fontSize: 18, color: FORM.color.ink4 }} />
              Upload a new document
            </Box>
            <Autocomplete
              multiple
              size="small"
              options={docOptions}
              value={pickedDocs}
              onChange={(_, next) =>
                onChange({ documentIds: next.map((o) => o.value) })
              }
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              noOptionsText="No documents uploaded yet"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={pickedDocs.length ? "" : "Select documents"}
                  sx={FIELD_SX}
                />
              )}
            />
          </PricingField>
        </Box>
        <FormTextField
          span={6}
          label="Notes"
          multiline
          minRows={2}
          value={form.notes}
          onChange={(v) => onChange({ notes: v })}
        />
        <FormTextField
          span={6}
          label="Note for history"
          value={form.note}
          onChange={(v) => onChange({ note: v })}
          helperText="Shown on this save's History entries, e.g. 'from the 10/14 email'."
        />
      </FormFieldGrid>
    </QuickEditDialog>
  );
}

ItemEditDialog.propTypes = {
  open: PropTypes.bool,
  onUpload: PropTypes.func,
  editing: PropTypes.object,
  statusOptions: PropTypes.array,
  documents: PropTypes.array,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

/* ─────────────────────── header: required documents ─────────────────────── */

// "OFAC check (performed by Resfin via the LOS)" → name + a quieter note, so
// the list reads as short document names.
const splitLabel = (label) => {
  const m = String(label || "").match(/^(.*?)\s*\((.+)\)\s*$/);
  return m ? { name: m[1], note: m[2] } : { name: String(label || ""), note: null };
};

// Rows a card shows before its list scrolls; "Show all" lifts the cap.
const CARD_PREVIEW = 5;
// One checklist row's height (py 6px + a 13px line + icon).
const CARD_ROW_H = 34;

/**
 * One list's card in the header: its name and agreement, a count + bar, and
 * its required documents as a checklist — open ones (hollow circle) first,
 * then done ones (green tick, with the day they were marked). Each line jumps
 * to that row in the table below. Every row is listed; the list scrolls past
 * the first few until "Show all" removes the height cap.
 */
function RequiredDocsCard({
  title,
  agreements,
  rows,
  done,
  total,
  expanded,
  onToggle,
  onJump,
}) {
  const open = rows.filter((r) => !r.done);
  const finished = rows.filter((r) => r.done);
  const capped = rows.length > CARD_PREVIEW;
  const complete = total > 0 && done === total;

  return (
    <Box
      sx={{
        border: `1px solid ${PT.line}`,
        borderRadius: "10px",
        bgcolor: "#fff",
        minWidth: 0,
      }}
    >
      <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25 }}>
        <Flx ac jb g={1}>
          <Flx ac g={0.75} sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 13.5, fontWeight: 600, color: PT.ink }}>
              {title}
            </Box>
            <SoftBadge tone="slate">{agreements}</SoftBadge>
          </Flx>
          <Box
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: complete ? statusColors.success.fg : PT.ink2,
              fontVariantNumeric: "tabular-nums",
              flexShrink: 0,
            }}
          >
            {`${done} / ${total}`}
          </Box>
        </Flx>
        <LinearProgress
          variant="determinate"
          value={total ? (done / total) * 100 : 0}
          sx={{
            mt: 1,
            height: 5,
            borderRadius: 999,
            bgcolor: PT.lineSoft,
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              bgcolor: complete ? statusColors.success.solid : PT.accent,
            },
          }}
        />
        <Box sx={{ mt: 0.75, fontSize: 12, color: PT.muted }}>
          {complete
            ? "All required documents are in."
            : `${open.length} still needed${
                finished.length ? ` · ${finished.length} done` : ""
              }`}
        </Box>
      </Box>

      {rows.length > 0 && (
        <Box
          component="ul"
          sx={{
            m: 0,
            p: 0,
            listStyle: "none",
            borderTop: `1px solid ${PT.lineSoft}`,
            // Scrolls past the first few until "Show all".
            ...(capped && !expanded
              ? { maxHeight: CARD_PREVIEW * CARD_ROW_H, overflowY: "auto" }
              : null),
          }}
        >
          {rows.map((r) => {
            const { name, note } = splitLabel(r.tpl.label);
            return (
              <Box component="li" key={r.tpl.id}>
                <ButtonBase
                  onClick={() => onJump(r.tpl.id)}
                  sx={{
                    width: "100%",
                    justifyContent: "flex-start",
                    textAlign: "left",
                    gap: 1,
                    px: 1.75,
                    py: 0.75,
                    minHeight: CARD_ROW_H,
                    "&:hover": { bgcolor: slate[50] },
                    "&:hover .rdGo": { opacity: 1 },
                  }}
                >
                  {r.done ? (
                    <CheckCircleRounded
                      sx={{ fontSize: 16, color: statusColors.success.solid, flexShrink: 0 }}
                    />
                  ) : (
                    <RadioButtonUncheckedRounded
                      sx={{ fontSize: 16, color: slate[300], flexShrink: 0 }}
                    />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 12.5,
                        color: r.done ? PT.muted : PT.ink,
                      }}
                    >
                      {name}
                    </Box>
                    {note && (
                      <Box component="span" sx={{ fontSize: 11.5, color: PT.faint }}>
                        {` · ${note}`}
                      </Box>
                    )}
                  </Box>
                  {r.done && r.item?.statusDate ? (
                    <Box sx={{ fontSize: 11.5, color: PT.faint, flexShrink: 0 }}>
                      {fmtWhen(r.item.statusDate)}
                    </Box>
                  ) : (
                    <ChevronRightRounded
                      className="rdGo"
                      sx={{ fontSize: 16, color: PT.faint, opacity: 0, flexShrink: 0 }}
                    />
                  )}
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
      )}

      {capped && (
        <ButtonBase
          onClick={onToggle}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            gap: 0.5,
            px: 1.75,
            py: 0.9,
            borderTop: `1px solid ${PT.lineSoft}`,
            fontSize: 12,
            fontWeight: 600,
            color: PT.accent,
            borderBottomLeftRadius: "10px",
            borderBottomRightRadius: "10px",
            "&:hover": { bgcolor: slate[50] },
          }}
        >
          {expanded ? "Show less" : `Show all ${rows.length}`}
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 16,
              transform: expanded ? "rotate(180deg)" : "none",
            }}
          />
        </ButtonBase>
      )}
    </Box>
  );
}

RequiredDocsCard.propTypes = {
  title: PropTypes.string,
  agreements: PropTypes.string,
  rows: PropTypes.array,
  done: PropTypes.number,
  total: PropTypes.number,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onJump: PropTypes.func,
};

/* ───────────────────────────── the tab ───────────────────────────── */

export default function CorrespondentOnboardingTab({
  detail,
  onProgress,
  onChanged,
  onOpenAgreements,
}) {
  const correspondentsApi = useCorrespondentsApi();
  const users = useSelector((s) => s.users);
  const { isEastview } = useEnv();
  const cid = docId(detail);

  const [view, setView] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(() => buildDraft(null));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [itemSaving, setItemSaving] = useState(false);
  const [itemError, setItemError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  // The item dialog steps aside while the upload panel is open (a drawer sits
  // under a modal), then comes back with the new document linked.
  const [uploading, setUploading] = useState(false);
  // The Prospect questionnaire's direct upload: { name, size, pct } while it
  // runs (pct null until the browser reports progress).
  const [qUpload, setQUpload] = useState(null);
  const [qUploadError, setQUploadError] = useState(null);
  // The upload response carries the updated documents[] — used until the
  // refreshed correspondent record arrives, so the new file resolves at once.
  const [uploadedDocs, setUploadedDocs] = useState(null);
  useEffect(() => setUploadedDocs(null), [detail]);

  // Documents are already on the correspondent record — the picker and the
  // linked-file names resolve against them without another fetch.
  const documents = useMemo(
    () => (uploadedDocs ?? detail?.documents ?? []).filter(Boolean),
    [detail, uploadedDocs],
  );
  const docsById = useMemo(
    () => Object.fromEntries(documents.map((d) => [oidOf(d), d])),
    [documents],
  );

  const applyView = useCallback(
    (v, { resetDraft }) => {
      setView(v);
      if (resetDraft) setDraft(buildDraft(v?.checklist));
      if (v?.progress) onProgress?.(v.progress);
    },
    [onProgress],
  );

  useEffect(() => {
    if (!cid) return;
    setLoadError(null);
    correspondentsApi
      .getOnboardingChecklist(cid)
      .then((v) => applyView(v, { resetDraft: true }))
      .catch((e) => setLoadError(errorMessage(e, "Couldn't load the checklist")));
  }, [cid, correspondentsApi, applyView]);

  const options = { ...FALLBACK_OPTIONS, ...(view?.options || {}) };
  const checklist = view?.checklist || {};
  const template = useMemo(() => view?.template ?? [], [view]);
  const progress = view?.progress;

  const baseline = useMemo(() => buildDraft(view?.checklist), [view]);
  const changedKeys = useMemo(
    () =>
      Object.keys(FIELDS).filter((k) => !sameValue(draft[k], baseline[k])),
    [draft, baseline],
  );
  const dirty = changedKeys.length > 0;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body = Object.fromEntries(
        changedKeys.map((k) => [k, toWire(FIELDS[k], draft[k])]),
      );
      // Eastview build: Channel is fixed to Eastview — write it with the
      // first save that finds it unset (or set to anything else).
      if (isEastview && checklist.channel !== EASTVIEW_CHANNEL)
        body.channel = EASTVIEW_CHANNEL;
      const v = await correspondentsApi.saveOnboardingChecklist(cid, body);
      applyView(v, { resetDraft: true });
    } catch (e) {
      setSaveError(errorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const uploadQuestionnaire = async (file) => {
    setQUploadError(null);
    setQUpload({ name: file.name, size: file.size, pct: null });
    try {
      const res = await correspondentsApi.uploadDocument(
        cid,
        file,
        { category: "Prospect Questionnaire" },
        {
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            setQUpload((u) => (u ? { ...u, pct } : u));
          },
        },
      );
      const next = (res?.documents ?? []).filter(Boolean);
      if (next.length) setUploadedDocs(next);
      // A questionnaire on file has come back — stamp received (and sent) if
      // they aren't set yet, saved right away: the section has no other
      // controls, so there's nothing to wait for.
      const received = baseline.questionnaireReceivedDate || todayInput();
      const sent = baseline.questionnaireSentDate || received;
      const stamp = {};
      if (!baseline.questionnaireReceivedDate)
        stamp.questionnaireReceivedDate = received;
      if (!baseline.questionnaireSentDate) stamp.questionnaireSentDate = sent;
      setDraft((d) => ({
        ...d,
        questionnaireReceivedDate: received,
        questionnaireSentDate: sent,
      }));
      if (Object.keys(stamp).length) {
        const v = await correspondentsApi.saveOnboardingChecklist(cid, stamp);
        applyView(v, { resetDraft: false });
      }
      // Refresh the record so the Agreements tab and the header see it.
      onChanged?.();
    } catch (e) {
      setQUploadError(errorMessage(e, "Upload failed."));
    } finally {
      setQUpload(null);
    }
  };

  /* ── document items ─────────────────────────────────────────────── */

  const sectionKeys = visibleSectionKeys(draft.agreementTypes);
  const itemRows = useMemo(
    () =>
      template
        .filter((t) => sectionKeys.includes(t.section))
        .map((t) => ({ tpl: t, item: checklist.items?.[t.id] || null })),
    // sectionKeys is derived from draft.agreementTypes each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template, checklist, sectionKeys.join(",")],
  );

  const openItem = (row) => {
    setItemError(null);
    setEditing({
      tpl: row.tpl,
      original: row.item || {},
      form: {
        status: row.item?.status || "Pending",
        statusDate: row.item?.statusDate || null,
        documentIds: [...(row.item?.documentIds || [])],
        value: row.item?.value || "",
        notes: row.item?.notes || "",
        note: "",
      },
    });
  };

  const saveItem = async () => {
    if (!editing) return;
    const { tpl, original, form } = editing;
    const patch = {};
    if ((form.status || "Pending") !== (original.status || "Pending"))
      patch.status = form.status || "Pending";
    // Send a date only when it was changed — the server stamps today otherwise.
    if (
      DATED_STATUSES.includes(form.status) &&
      toDateInput(form.statusDate) !== toDateInput(original.statusDate)
    )
      patch.statusDate = form.statusDate || null;
    if (!sameValue(form.documentIds, original.documentIds || []))
      patch.documentIds = form.documentIds;
    if ((form.value || "") !== (original.value || ""))
      patch.value = form.value || null;
    if ((form.notes || "") !== (original.notes || ""))
      patch.notes = form.notes || null;
    if (!Object.keys(patch).length) {
      setEditing(null);
      return;
    }
    setItemSaving(true);
    setItemError(null);
    try {
      const body = { items: { [tpl.id]: patch } };
      if (form.note?.trim()) body.note = form.note.trim();
      const v = await correspondentsApi.saveOnboardingChecklist(cid, body);
      // The field draft may hold unsaved edits — keep it.
      applyView(v, { resetDraft: false });
      setEditing(null);
    } catch (e) {
      setItemError(errorMessage(e, "Save failed"));
    } finally {
      setItemSaving(false);
    }
  };

  /* ── quick row actions (no dialog) ──────────────────────────────── */
  // The same one-step feel as the Agreements tab: upload from the row (or drop
  // a file on it), change status from the chip, link an existing file from the
  // Documents cell. Each is ONE item save, immediately — the pencil dialog
  // stays for notes, the warehouse lender name, backdating and history notes.

  // Item ids with a save in flight (the row dims and ignores clicks).
  const [rowBusy, setRowBusy] = useState(() => new Set());
  const [rowError, setRowError] = useState(null); // { section, msg }
  // { row, file? } — the upload panel opened from a row (file = dropped).
  const [rowUpload, setRowUpload] = useState(null);
  const [statusMenu, setStatusMenu] = useState(null); // { anchor, row }
  const [linkMenu, setLinkMenu] = useState(null); // { anchor, row }
  const [dragRowId, setDragRowId] = useState(null);
  // The row a header checklist item jumped to, highlighted for a moment.
  const [flashRowId, setFlashRowId] = useState(null);
  // Header lists opened past their first few open documents (section keys).
  const [expandedLists, setExpandedLists] = useState([]);

  const quickSaveItem = useCallback(
    async (row, patch) => {
      const id = row.tpl.id;
      setRowError(null);
      setRowBusy((s) => new Set(s).add(id));
      try {
        const v = await correspondentsApi.saveOnboardingChecklist(cid, {
          items: { [id]: patch },
        });
        // The field draft may hold unsaved edits — keep it.
        applyView(v, { resetDraft: false });
      } catch (e) {
        setRowError({
          section: row.tpl.section,
          msg: `${row.tpl.label}: ${errorMessage(e, "Save failed")}`,
        });
      } finally {
        setRowBusy((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
      }
    },
    [cid, correspondentsApi, applyView],
  );

  // A file uploaded from a row is linked to it, and the row moves to its done
  // status (Received, or Completed for the checks we run) in the same save.
  const onRowUploaded = (row, res) => {
    const before = new Set(documents.map(oidOf));
    const next = (res?.documents ?? []).filter(Boolean);
    const added = next.map(oidOf).filter((id) => id && !before.has(id));
    if (next.length) setUploadedDocs(next);
    setRowUpload(null);
    // Refresh the record so the Agreements tab and the header see it.
    onChanged?.();
    if (!added.length) return;
    const patch = {
      documentIds: [...new Set([...(row.item?.documentIds || []), ...added])],
    };
    const doneStatus = row.tpl.doneStatus || "Received";
    if ((row.item?.status || "Pending") !== doneStatus) patch.status = doneStatus;
    quickSaveItem(row, patch);
  };

  const uploadCategoryOf = (tpl) =>
    ITEM_UPLOAD_CATEGORY[tpl.key] ||
    ITEM_UPLOAD_CATEGORY[String(tpl.id).split(".")[1]] ||
    "Other";

  // File drop on a row: the grid has no per-row file events, so the drop is
  // caught on the wrapper and resolved to the row under the pointer.
  const rowIdFromEvent = (e) =>
    e.target?.closest?.(".ag-row")?.getAttribute("row-id") || null;
  const hasFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");
  const dropProps = (rows) => ({
    onDragOver: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      const id = rowIdFromEvent(e);
      if (id !== dragRowId) setDragRowId(id);
    },
    onDragLeave: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setDragRowId(null);
    },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragRowId(null);
      const row = rows.find((r) => r.tpl.id === rowIdFromEvent(e));
      const file = e.dataTransfer.files?.[0];
      if (row && file && !rowBusy.has(row.tpl.id)) setRowUpload({ row, file });
    },
  });

  // Cell renderers read the latest handlers through a ref, so the column defs
  // (and the grid's sort / widths) don't rebuild on every render.
  const rowActions = useRef({});
  rowActions.current = {
    openItem,
    openUpload: (row) => setRowUpload({ row, file: null }),
    openStatusMenu: (anchor, row) => setStatusMenu({ anchor, row }),
    openLinkMenu: (anchor, row) => setLinkMenu({ anchor, row }),
    previewDoc: setPreviewDoc,
  };

  const itemCols = useMemo(
    () => [
      {
        headerName: "Item",
        flex: 2,
        minWidth: 240,
        valueGetter: (p) => p.data?.tpl?.label,
        // A–Z by default; every column can be re-sorted from its header.
        sort: "asc",
        comparator: (a, b) => String(a || "").localeCompare(String(b || "")),
        cellRenderer: (p) => (
          <Flx ac g={1} sx={{ height: "100%", minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: PT.ink,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {p.value}
            </Box>
            {!p.data?.tpl?.required && (
              <Box sx={{ flexShrink: 0 }}>
                <SoftBadge tone="slate">Optional</SoftBadge>
              </Box>
            )}
          </Flx>
        ),
      },
      {
        headerName: "Status",
        width: 130,
        valueGetter: (p) => p.data?.item?.status || "Pending",
        cellRenderer: (p) => {
          const done = isItemDone(p.data.tpl, p.data.item);
          const tone =
            p.value === "N/A"
              ? ITEM_STATUS_TONE["N/A"]
              : done
                ? ITEM_STATUS_TONE.done
                : p.value === "Pending"
                  ? ITEM_STATUS_TONE.Pending
                  : ITEM_STATUS_TONE.other;
          // The chip is the status control — one click opens the choices.
          return (
            <Flx ac sx={{ height: "100%" }}>
              <Tooltip title="Change status">
                <ButtonBase
                  onClick={(e) =>
                    rowActions.current.openStatusMenu(e.currentTarget, p.data)
                  }
                  aria-label={`Status: ${p.value}. Change status`}
                  sx={{
                    borderRadius: "999px",
                    "&:hover": { filter: "brightness(0.96)" },
                    "&:focus-visible": {
                      outline: `2px solid ${PT.accent}`,
                      outlineOffset: 1,
                    },
                  }}
                >
                  <SoftBadge
                    tone={tone}
                    icon={done && p.value !== "N/A" ? CheckRounded : undefined}
                  >
                    <Flx ac component="span" sx={{ gap: "2px" }}>
                      {p.value}
                      <KeyboardArrowDownRounded sx={{ fontSize: 14, mr: "-3px" }} />
                    </Flx>
                  </SoftBadge>
                </ButtonBase>
              </Tooltip>
            </Flx>
          );
        },
      },
      {
        headerName: "Marked",
        width: 200,
        valueGetter: (p) => p.data?.item?.statusDate || "",
        cellRenderer: (p) =>
          p.data?.item?.statusBy || p.data?.item?.statusDate ? (
            <Flx column jc sx={{ height: "100%", minWidth: 0, lineHeight: 1.35 }}>
              <Box sx={{ fontSize: 12.5, color: PT.ink2 }}>
                {fmtWhen(p.data.item.statusDate) || "—"}
              </Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  color: PT.muted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {p.data.item.statusBy || "—"}
              </Box>
            </Flx>
          ) : (
            <Flx ac sx={{ height: "100%", color: PT.faint, fontSize: 12.5 }}>
              —
            </Flx>
          ),
      },
      {
        headerName: "Documents",
        flex: 1.6,
        minWidth: 200,
        // Sorts by how many documents are linked.
        valueGetter: (p) => (p.data?.item?.documentIds || []).length,
        cellRenderer: (p) => {
          const docs = (p.data?.item?.documentIds || [])
            .map((id) => docsById[id])
            .filter(Boolean);
          const shown = docs.slice(0, 2);
          const more = docs.slice(2);
          return (
            <Flx ac g={1} sx={{ height: "100%", minWidth: 0 }}>
              {shown.map((d, i) => (
                <Flx ac key={oidOf(d) || i} sx={{ minWidth: 0, flexShrink: 1 }}>
                  <DocLink doc={d} onOpen={rowActions.current.previewDoc} />
                </Flx>
              ))}
              {more.length > 0 && (
                <Tooltip
                  title={more
                    .map((d) => d.file_display_name || d.file_name_as_loaded)
                    .join(", ")}
                >
                  <Box sx={{ flexShrink: 0, fontSize: 11.5, color: PT.muted }}>
                    +{more.length}
                  </Box>
                </Tooltip>
              )}
              {/* Link a file that's already on the correspondent (e.g. one
                  uploaded from the Agreements tab). */}
              <Tooltip title="Link a file already on file">
                <ButtonBase
                  onClick={(e) =>
                    rowActions.current.openLinkMenu(e.currentTarget, p.data)
                  }
                  aria-label="Link a file already on file"
                  sx={{
                    flexShrink: 0,
                    gap: "3px",
                    px: "6px",
                    py: "2px",
                    borderRadius: "6px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: docs.length ? PT.faint : PT.muted,
                    "&:hover": { color: PT.accent, bgcolor: slate[50] },
                  }}
                >
                  <AddLinkRounded sx={{ fontSize: 15 }} />
                  {docs.length ? null : "Link"}
                </ButtonBase>
              </Tooltip>
            </Flx>
          );
        },
      },
      {
        headerName: "Notes",
        flex: 1.4,
        minWidth: 160,
        valueGetter: (p) =>
          [p.data?.item?.value, p.data?.item?.notes].filter(Boolean).join(" · "),
        tooltipValueGetter: (p) => p.value || null,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%", minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 12.5,
                color: p.value ? PT.ink2 : PT.faint,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {p.value || "—"}
            </Box>
          </Flx>
        ),
      },
      actionColumn({
        colId: "edit",
        count: 2,
        cellRenderer: (p) => (
          <ActionCell>
            <Tooltip title="Upload a file for this item">
              <IconButton
                onClick={() => rowActions.current.openUpload(p.data)}
                aria-label="Upload a file for this item"
              >
                <UploadFileOutlined sx={{ fontSize: 17, color: PT.muted }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit item">
              <IconButton
                onClick={() => rowActions.current.openItem(p.data)}
                aria-label="Edit item"
              >
                <EditOutlined sx={{ fontSize: 17, color: PT.muted }} />
              </IconButton>
            </Tooltip>
          </ActionCell>
        ),
      }),
    ],
    [docsById],
  );

  /* ── history ────────────────────────────────────────────────────── */

  const templateById = useMemo(
    () => Object.fromEntries(template.map((t) => [t.id, t])),
    [template],
  );

  const fieldLabel = useCallback(
    (field) => {
      const f = String(field || "");
      if (f.startsWith("items.")) {
        const [, section, key, attr] = f.split(".");
        const tpl = templateById[`${section}.${key}`];
        const name = tpl ? tpl.label : key;
        return `${name} (${SECTION_SHORT[section] || section}) ${
          ITEM_ATTR_LABELS[attr] || attr
        }`;
      }
      return FIELD_LABELS[f] || f;
    },
    [templateById],
  );

  const fmtAuditValue = useCallback(
    (field, v) => {
      if (v == null || v === "" || (Array.isArray(v) && !v.length)) return "—";
      if (typeof v === "boolean") return v ? "Yes" : "No";
      if (String(field).endsWith(".documentIds") && Array.isArray(v))
        return v
          .map(
            (id) =>
              docsById[id]?.file_display_name ||
              docsById[id]?.file_name_as_loaded ||
              "a removed document",
          )
          .join(", ");
      if (Array.isArray(v)) return v.join(", ");
      if (isDateish(v)) return fmtWhen(v);
      return String(v);
    },
    [docsById],
  );

  const historyRows = useMemo(
    () =>
      (view?.auditTrail ?? []).map((e, i) => ({
        ...e,
        _key: `${e.changedDateTime || ""}-${e.field}-${i}`,
        change: `${fieldLabel(e.field)}: ${fmtAuditValue(
          e.field,
          e.oldValue,
        )} → ${fmtAuditValue(e.field, e.newValue)}`,
      })),
    [view, fieldLabel, fmtAuditValue],
  );

  const historyCols = useMemo(
    () => [
      {
        headerName: "When",
        width: 170,
        valueGetter: (p) => p.data?.changedDateTime,
        valueFormatter: (p) => (p.value ? formatDateTime(p.value) : "—"),
      },
      {
        headerName: "Who",
        field: "changedBy",
        width: 220,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Change",
        field: "change",
        flex: 2,
        minWidth: 260,
        tooltipField: "change",
      },
      {
        headerName: "Note",
        field: "note",
        flex: 1,
        minWidth: 160,
        tooltipField: "note",
        valueFormatter: (p) => p.value || "—",
      },
    ],
    [],
  );

  /* ── render ─────────────────────────────────────────────────────── */

  if (loadError)
    return (
      <Box sx={{ fontSize: 13, color: statusColors.error.fg }}>{loadError}</Box>
    );
  if (!view)
    return <Box sx={{ fontSize: 13, color: PT.faint }}>Loading checklist…</Box>;

  const contact = detail?.primaryContact || {};
  const questionnaireDocs = documents.filter(
    (d) => d.category === "Prospect Questionnaire",
  );
  const latestQuestionnaire =
    [...questionnaireDocs].sort((a, b) =>
      String(b.uploadDate || "").localeCompare(String(a.uploadDate || "")),
    )[0] || null;
  const questionnaireBadge = latestQuestionnaire ? (
    <SectionBadge tone="green">On file</SectionBadge>
  ) : draft.questionnaireReceivedDate ? (
    <SectionBadge tone="green">
      Received {shortDay(draft.questionnaireReceivedDate)}
    </SectionBadge>
  ) : draft.questionnaireSentDate ? (
    <SectionBadge tone="amber">
      Sent {shortDay(draft.questionnaireSentDate)} · awaiting
    </SectionBadge>
  ) : null;
  // Each card's progress: how many of the things it asks for are filled (live,
  // from the draft — so it moves as you tick, before saving). A card is
  // "complete" (green check) when all are; otherwise it shows an in-progress
  // ring and "n of m".
  const has = (v) => v !== "" && v != null && v !== false;
  const tally = (...checks) => ({
    done: checks.filter(Boolean).length,
    total: checks.length,
  });
  const progressOf = {
    engagement: tally(
      has(detail?.companyName),
      has(contact.fullName || contact.emailAddress),
      isEastview || has(draft.channel),
      has(draft.clientType),
      has(draft.ndaSentDate),
    ),
    // One step now: the questionnaire is on file (an older record may only
    // carry the received date).
    questionnaire: tally(
      !!latestQuestionnaire || has(draft.questionnaireReceivedDate),
    ),
    review: tally(has(draft.reviewedBy), has(draft.reviewDate)),
    compliance: tally(
      draft.agreementTypes.length > 0,
      has(draft.agreementSentDate),
      has(draft.agreementExecutedDate),
      ...(draft.amaApplicable
        ? [has(draft.amaSentDate), has(draft.amaExecutedDate)]
        : []),
    ),
    golive: tally(
      has(draft.notificationEmailSentDate),
      draft.addedToTracker === "Yes",
      has(draft.relationshipManager),
      draft.relationshipManagerSignOff === "Yes",
      has(draft.relationshipManagerSignOffDate),
      has(draft.finalSignedOffBy),
      has(draft.finalSignOffDate),
    ),
  };
  const complete = Object.fromEntries(
    Object.entries(progressOf).map(([k, p]) => [k, p.done === p.total]),
  );

  const ndaBadge = draft.ndaSentDate ? (
    <SectionBadge tone="green">NDA sent {shortDay(draft.ndaSentDate)}</SectionBadge>
  ) : null;

  // The header's per-list summary: every REQUIRED document in each visible
  // list (both until an agreement is picked), open ones first, from the same
  // rows the tables show.
  const visibleSections = SECTIONS.filter((s) => sectionKeys.includes(s.key));
  const requiredBySection = visibleSections.map((s) => {
    const rows = itemRows
      .filter((r) => r.tpl.section === s.key && r.tpl.required)
      .map((r) => ({ ...r, done: isItemDone(r.tpl, r.item) }))
      .sort(
        (x, y) =>
          Number(x.done) - Number(y.done) ||
          String(x.tpl.label).localeCompare(String(y.tpl.label)),
      );
    return {
      ...s,
      rows,
      total: rows.length,
      done: rows.filter((r) => r.done).length,
    };
  });
  const totalRequired = requiredBySection.reduce((n, g) => n + g.total, 0);
  const totalDone = requiredBySection.reduce((n, g) => n + g.done, 0);
  const requiredStatus = {
    complete: totalRequired > 0 && totalDone === totalRequired,
    progress: { done: totalDone, total: totalRequired },
  };

  // "On this page" with each section's own check / progress ring — the same
  // status its card header shows. History has none.
  const railStatus = {
    "onb-required": requiredStatus,
    "onb-documents": requiredStatus,
    "onb-engagement": { complete: complete.engagement, progress: progressOf.engagement },
    "onb-questionnaire": { complete: complete.questionnaire, progress: progressOf.questionnaire },
    "onb-review": { complete: complete.review, progress: progressOf.review },
    "onb-execution": { complete: complete.compliance, progress: progressOf.compliance },
    "onb-golive": { complete: complete.golive, progress: progressOf.golive },
  };
  const rail = RAIL.map((it) =>
    railStatus[it.id] ? { ...it, status: railStatus[it.id] } : it,
  );

  // Scroll to an item's table and flash its row.
  const jumpToItem = (itemId) => {
    const section = String(itemId).split(".")[0];
    const idx = visibleSections.findIndex((s) => s.key === section);
    const el = document.getElementById(
      idx === 0 ? "onb-documents" : `onb-documents-${section}`,
    );
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    setFlashRowId(itemId);
    window.setTimeout(() => setFlashRowId(null), 2000);
  };
  const jumpToCompliance = () =>
    document
      .getElementById("onb-execution")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      <PricingPageLayout nav={rail}>
        {/* ── 0. Required documents — a section like the rest: same card,
            status mark, collapse and rail entry. ─────────────────────── */}
        <ProfileSection
          id="onb-required"
          title="Required documents"
          complete={requiredStatus.complete}
          progress={requiredStatus.progress}
          desc={`Template ${checklist.templateVersion || "—"}${
            checklist.updatedAt
              ? ` · last saved ${formatDateTime(checklist.updatedAt)}${
                  checklist.updatedBy ? ` by ${checklist.updatedBy}` : ""
                }`
              : ""
          }`}
          badge={
            progress?.finalSignedOff ? (
              <SoftBadge tone="green" icon={CheckRounded}>
                Signed off
              </SoftBadge>
            ) : null
          }
          actions={
            <Box
              component="a"
              href={ONBOARDING_BLANK_FORMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                color: PT.accent,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Blank forms
              <OpenInNewRounded sx={{ fontSize: 14 }} />
            </Box>
          }
        >

          {/* Why there are two lists (and 18 documents) before an agreement
              is picked — and where to make it one. */}
          {!draft.agreementTypes.length && requiredBySection.length > 1 && (
            <Box
              sx={{
                mb: 1.75,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                bgcolor: slate[50],
                border: `1px solid ${PT.lineSoft}`,
                fontSize: 12.5,
                color: PT.ink2,
                lineHeight: 1.5,
              }}
            >
              Both lists are shown because no agreement has been picked yet.
              Choose the agreement in{" "}
              <ButtonBase
                onClick={jumpToCompliance}
                sx={{
                  font: "inherit",
                  fontWeight: 600,
                  color: PT.accent,
                  verticalAlign: "baseline",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Compliance
              </ButtonBase>{" "}
              and only the list that applies will remain.
            </Box>
          )}

          {/* One card per list: its own count and bar, and its required
              documents as a short checklist (open first). */}
          {requiredBySection.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md:
                    requiredBySection.length > 1
                      ? "repeat(2, minmax(0, 1fr))"
                      : "minmax(0, 1fr)",
                },
                alignItems: "start",
              }}
            >
              {requiredBySection.map((g) => (
                <RequiredDocsCard
                  key={g.key}
                  title={g.short}
                  agreements={g.agreements.join(" / ")}
                  rows={g.rows}
                  done={g.done}
                  total={g.total}
                  expanded={expandedLists.includes(g.key)}
                  onToggle={() =>
                    setExpandedLists((l) =>
                      l.includes(g.key)
                        ? l.filter((k) => k !== g.key)
                        : [...l, g.key],
                    )
                  }
                  onJump={jumpToItem}
                />
              ))}
            </Box>
          )}
          {saveError && (
            <Box sx={{ ...FIELD_TEXT.helper, fontStyle: "normal", color: statusColors.error.fg }}>
              {saveError}
            </Box>
          )}
        </ProfileSection>

        {/* ── 1. Initial engagement (NDA as a side panel) ─────────── */}
        <ProfileSection
          id="onb-engagement"
          title="Initial engagement"
          complete={complete.engagement}
          progress={progressOf.engagement}
          desc={
            isEastview
              ? "Correspondent = pre-close (LFA) and/or post-close (MLPA). Broker = referral only (Broker Agreement)."
              : "Correspondent = Eastview channel, pre-close (LFA) and/or post-close (MLPA). BOP = Lendmarq channel, pre-close only (LFA). Broker = referral only (Broker Agreement)."
          }
          badge={ndaBadge}
        >
          <WithSidePanel
            panel={
              <>
                {/* Same shape as the AMA's Applies: one check box, and once
                    it's ticked the date it went out (today by default).
                    "NDA drafted" was dropped — sending one still records it
                    as drafted on the record. */}
                <Flx column g={2.5}>
                  <Box>
                    <ColumnHead>NDA</ColumnHead>
                    <OptionChip
                      label="Sent"
                      checked={!!draft.ndaSentDate}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          ndaSentDate: d.ndaSentDate ? "" : todayInput(),
                          // Sending one implies it was drafted.
                          ndaDrafted: d.ndaSentDate ? d.ndaDrafted : "Yes",
                        }))
                      }
                    />
                  </Box>
                  {draft.ndaSentDate && (
                    <PricingField label="Sent date">
                      <DateInput
                        value={draft.ndaSentDate}
                        // Clearing the date keeps it sent (today) — untick
                        // Sent to clear.
                        onChange={(v) => set("ndaSentDate", v || todayInput())}
                      />
                    </PricingField>
                  )}
                </Flx>
              </>
            }
          >
            <Grid>
              <Cell span={6} spanSm={6}>
                <PricingField label="Counterparty">
                  <ReadOnlyValue>{detail?.companyName}</ReadOnlyValue>
                </PricingField>
              </Cell>
              <Cell span={6} spanSm={6}>
                <PricingField
                  label="Primary contact"
                  helperText="From the Contacts tab."
                >
                  <ReadOnlyValue>
                    {[contact.fullName, contact.emailAddress]
                      .filter(Boolean)
                      .join(" · ")}
                  </ReadOnlyValue>
                </PricingField>
              </Cell>
              <Cell span={6} spanSm={6}>
                <PricingField label="Channel">
                  {isEastview ? (
                    <ReadOnlyValue>{EASTVIEW_CHANNEL}</ReadOnlyValue>
                  ) : (
                    <SelectInput
                      value={draft.channel}
                      onChange={(v) => set("channel", v)}
                      options={options.channel}
                    />
                  )}
                </PricingField>
              </Cell>
              <Cell span={6} spanSm={6}>
                <PricingField label="Client type">
                  {/* Segmented toggle, same as Profile's Relationship status:
                      "No Type" is selected when nothing is set and saves as
                      null. Values are the backend's options verbatim. */}
                  <ToggleButtonGroup
                    exclusive
                    value={draft.clientType || NO_CLIENT_TYPE}
                    onChange={(_, v) => {
                      // Clicking the selected option again reports null — ignore.
                      if (v == null) return;
                      set("clientType", v === NO_CLIENT_TYPE ? "" : v);
                    }}
                    aria-label="Client type"
                    sx={{
                      ...segmentedGroupSx({ fontSize: 13 }),
                      alignSelf: "flex-start",
                      flexWrap: "wrap",
                      border: `1px solid ${PT.line}`,
                    }}
                  >
                    {[
                      NO_CLIENT_TYPE,
                      ...(isEastview
                        ? options.clientType.filter(
                            (o) =>
                              EASTVIEW_CLIENT_TYPES.includes(o) ||
                              o === draft.clientType,
                          )
                        : options.clientType),
                    ].map((o) => (
                      <ToggleButton key={o} value={o}>
                        {o === NO_CLIENT_TYPE ? "No Type" : o}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </PricingField>
              </Cell>
            </Grid>

          </WithSidePanel>
        </ProfileSection>

        {/* ── 2. Prospect questionnaire ───────────────────────────── */}
        <ProfileSection
          id="onb-questionnaire"
          title="Prospect questionnaire"
          complete={complete.questionnaire}
          progress={progressOf.questionnaire}
          desc="Drop the completed questionnaire here. Uploading it marks the questionnaire as sent and received."
          badge={questionnaireBadge}
        >
          {/* File only — no sent / received boxes. The upload stamps both
              dates (see uploadQuestionnaire). */}
          <Box sx={{ maxWidth: "100%" }}>
                <FileSlot
                  fallbackName="Questionnaire"
                  doc={latestQuestionnaire}
                  uploading={qUpload}
                  error={qUploadError}
                  onFile={uploadQuestionnaire}
                  onPreview={setPreviewDoc}
                />
                <Box sx={{ mt: 1, fontSize: 12.5, color: slate[500] }}>
                  Uploads also appear on the{" "}
                  {onOpenAgreements ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={onOpenAgreements}
                      sx={{
                        border: "none",
                        background: "none",
                        p: 0,
                        font: "inherit",
                        color: teal[700],
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Agreements tab
                    </Box>
                  ) : (
                    "Agreements tab"
                  )}
                  .
                </Box>
          </Box>
        </ProfileSection>

        {/* ── 3. Senior & legal review ────────────────────────────── */}
        <ProfileSection
          id="onb-review"
          title="Senior & legal review"
          complete={complete.review}
          progress={progressOf.review}
          desc="Who reviewed the counterparty, and when."
        >
          <Grid>
            <Cell span={4} spanSm={6}>
              <PricingField label="Reviewed by">
                <EmailInput
                  value={draft.reviewedBy}
                  onChange={(v) => set("reviewedBy", v)}
                  users={users}
                />
              </PricingField>
            </Cell>
            <Cell span={4} spanSm={6}>
              <PricingField label="Review date">
                <DateInput
                  value={draft.reviewDate}
                  onChange={(v) => set("reviewDate", v)}
                />
              </PricingField>
            </Cell>
          </Grid>
        </ProfileSection>

        {/* ── 4. Agreement execution ──────────────────────────────── */}
        <ProfileSection
          id="onb-execution"
          title="Compliance"
          complete={complete.compliance}
          progress={progressOf.compliance}
          desc="Which agreement applies, when it went out and was executed, and whether an Asset Management Agreement applies. MLPA brings in the Post-Close documents; LFA or Broker Agreement brings in Pre-Close."
        >
          {/* Two halves like the engagement / NDA layout: the agreement on the
              left, the Asset Management Agreement on the right. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 1fr) 1px minmax(0, 1fr)",
              },
              gap: 3,
            }}
          >
            <Flx column g={2.5} sx={{ minWidth: 0 }}>
              {/* The SAME list the Profile tab's Legal Agreements check boxes
                  edit (agreementTypes) — set it in either place. More than one
                  can apply (an MLPA and an LFA); "No agreement" clears them. */}
              <Box>
                <LabelWithHint
                  label="Agreement"
                  hint="Select all that apply. Synced with Legal Agreements on the Profile tab."
                />
                <Flx wrap g={1} role="group" aria-label="Agreement">
                  <OptionChip
                    radio
                    label="No agreement"
                    checked={draft.agreementTypes.length === 0}
                    onClick={() => set("agreementTypes", [])}
                  />
                  {options.agreementTypes.map((o) => {
                    const on = draft.agreementTypes.includes(o);
                    return (
                      <OptionChip
                        key={o}
                        label={o}
                        checked={on}
                        onClick={() =>
                          set(
                            "agreementTypes",
                            // Kept in the options' order.
                            options.agreementTypes.filter((a) =>
                              a === o ? !on : draft.agreementTypes.includes(a),
                            ),
                          )
                        }
                      />
                    );
                  })}
                </Flx>
              </Box>
              <Grid>
                <Cell span={6} spanSm={6}>
                  <PricingField label="Agreement sent">
                    <DateInput
                      value={draft.agreementSentDate}
                      onChange={(v) => set("agreementSentDate", v)}
                    />
                  </PricingField>
                </Cell>
                <Cell span={6} spanSm={6}>
                  <PricingField label="Agreement executed">
                    <DateInput
                      value={draft.agreementExecutedDate}
                      onChange={(v) => set("agreementExecutedDate", v)}
                    />
                  </PricingField>
                </Cell>
              </Grid>
            </Flx>

            <Box sx={{ display: { xs: "none", md: "block" }, background: slate[200] }} />

            <Flx column g={2.5} sx={{ minWidth: 0 }}>
              <Box>
                <LabelWithHint
                  label="Asset Management Agreement"
                  hint="Required for RTL, ground-up construction, and other draw / rehab loans."
                />
                {/* One check box — ticked shows the AMA dates. */}
                <OptionChip
                  label="Applies"
                  checked={!!draft.amaApplicable}
                  onClick={() => set("amaApplicable", !draft.amaApplicable)}
                />
              </Box>
              {draft.amaApplicable && (
                <Grid>
                  <Cell span={6} spanSm={6}>
                    <PricingField label="AMA sent">
                      <DateInput
                        value={draft.amaSentDate}
                        onChange={(v) => set("amaSentDate", v)}
                      />
                    </PricingField>
                  </Cell>
                  <Cell span={6} spanSm={6}>
                    <PricingField label="AMA executed">
                      <DateInput
                        value={draft.amaExecutedDate}
                        onChange={(v) => set("amaExecutedDate", v)}
                      />
                    </PricingField>
                  </Cell>
                </Grid>
              )}
            </Flx>
          </Box>
        </ProfileSection>

        {/* ── 5. Post-execution documentation ─────────────────────── */}
        {SECTIONS.filter((s) => sectionKeys.includes(s.key)).map((s, i) => {
          const rows = itemRows.filter((r) => r.tpl.section === s.key);
          const required = rows.filter((r) => r.tpl.required);
          const done = required.filter((r) => isItemDone(r.tpl, r.item)).length;
          return (
            <ProfileSection
              key={s.key}
              id={i === 0 ? "onb-documents" : `onb-documents-${s.key}`}
              title={`Post-execution documentation · ${s.title}`}
              complete={required.length > 0 && done === required.length}
              progress={{ done, total: required.length }}
              desc={`${done} of ${required.length} required items done. ${
                draft.agreementTypes.length
                  ? "N/A counts as done; optional items never count."
                  : "Both sections show until an agreement type is chosen."
              } Drop a file on a row, or use its upload button, to attach it and mark the item done.`}
            >
              {rowError?.section === s.key && (
                <Box
                  role="alert"
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    fontSize: 12.5,
                    color: statusColors.error.body,
                    bgcolor: statusColors.error.bg,
                    border: `1px solid ${statusColors.error.bg}`,
                  }}
                >
                  {rowError.msg}
                </Box>
              )}
              <Box
                {...dropProps(rows)}
                sx={{
                  // The row a file is being dragged over, the row just jumped
                  // to, and rows mid-save.
                  ...(flashRowId
                    ? {
                        [`& .ag-row[row-id="${flashRowId}"]`]: {
                          bgcolor: `${teal[50]} !important`,
                          transition: "background-color .3s",
                        },
                      }
                    : null),
                  ...(dragRowId
                    ? {
                        [`& .ag-row[row-id="${dragRowId}"]`]: {
                          bgcolor: `${teal[50]} !important`,
                          outline: `2px dashed ${PT.accent}`,
                          outlineOffset: "-2px",
                        },
                      }
                    : null),
                  ...Object.fromEntries(
                    [...rowBusy].map((id) => [
                      `& .ag-row[row-id="${id}"]`,
                      { opacity: 0.55, pointerEvents: "none" },
                    ]),
                  ),
                }}
              >
                <AgGridTableWrapper compactTable>
                  <AgGridReact
                    rowData={rows}
                    columnDefs={itemCols}
                    getRowId={(p) => p.data.tpl.id}
                    defaultColDef={{
                      resizable: true,
                      sortable: true,
                      suppressHeaderMenuButton: true,
                    }}
                    domLayout="autoHeight"
                    rowHeight={46}
                    headerHeight={38}
                    tooltipShowDelay={300}
                    suppressCellFocus
                  />
                </AgGridTableWrapper>
              </Box>
            </ProfileSection>
          );
        })}

        {/* ── 6. Internal notification & go-live ──────────────────── */}
        <ProfileSection
          id="onb-golive"
          title="Internal notification & go-live"
          complete={complete.golive}
          progress={progressOf.golive}
          desc="The internal hand-off, relationship-manager sign-off and the final sign-off that completes onboarding."
        >
          <Grid>
            <Cell span={3} spanSm={6}>
              <PricingField label="Notification email sent">
                <DateInput
                  value={draft.notificationEmailSentDate}
                  onChange={(v) => set("notificationEmailSentDate", v)}
                />
              </PricingField>
            </Cell>
            <Cell span={3} spanSm={6}>
              <PricingField label="Added to tracker">
                <SelectInput
                  value={draft.addedToTracker}
                  onChange={(v) => set("addedToTracker", v)}
                  options={options.addedToTracker}
                />
              </PricingField>
            </Cell>
            <Cell span={6} spanSm={12}>
              <PricingField label="Relationship manager">
                <EmailInput
                  value={draft.relationshipManager}
                  onChange={(v) => set("relationshipManager", v)}
                  users={users}
                />
              </PricingField>
            </Cell>
            <Cell span={3} spanSm={6}>
              <PricingField label="RM sign-off">
                <SelectInput
                  value={draft.relationshipManagerSignOff}
                  onChange={(v) => set("relationshipManagerSignOff", v)}
                  options={["Yes", "No"]}
                />
              </PricingField>
            </Cell>
            <Cell span={3} spanSm={6}>
              <PricingField label="RM sign-off date">
                <DateInput
                  value={draft.relationshipManagerSignOffDate}
                  onChange={(v) => set("relationshipManagerSignOffDate", v)}
                />
              </PricingField>
            </Cell>
            <Cell span={4} spanSm={6}>
              <PricingField label="Final signed off by">
                <EmailInput
                  value={draft.finalSignedOffBy}
                  onChange={(v) => set("finalSignedOffBy", v)}
                  users={users}
                />
              </PricingField>
            </Cell>
            <Cell span={2} spanSm={6}>
              <PricingField label="Final sign-off date">
                <DateInput
                  value={draft.finalSignOffDate}
                  onChange={(v) => set("finalSignOffDate", v)}
                />
              </PricingField>
            </Cell>
          </Grid>
        </ProfileSection>

        {/* ── History ──────────────────────────────────────────────── */}
        <ProfileSection
          id="onb-history"
          title={`History (${historyRows.length})`}
          desc="Every saved change, field by field, newest first."
          defaultOpen={historyRows.length > 0}
        >
          {historyRows.length ? (
            <AgGridTableWrapper compactTable height="360px">
              <AgGridReact
                rowData={historyRows}
                columnDefs={historyCols}
                getRowId={(p) => p.data._key}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  suppressHeaderMenuButton: true,
                }}
                rowHeight={40}
                headerHeight={38}
                tooltipShowDelay={300}
                pagination={historyRows.length > 50}
                paginationPageSize={50}
                suppressCellFocus
              />
            </AgGridTableWrapper>
          ) : (
            <Box sx={{ fontSize: 13, color: PT.faint }}>No changes saved yet.</Box>
          )}
        </ProfileSection>
      </PricingPageLayout>

      <PendingChangesBar
        dirty={dirty}
        saving={saving}
        onSave={save}
        onDiscard={() => {
          setSaveError(null);
          setDraft(buildDraft(view?.checklist));
        }}
        label="Save checklist"
        count={changedKeys.length}
        subText={saveError || undefined}
      />

      <ItemEditDialog
        open={!!editing && !uploading}
        onUpload={() => setUploading(true)}
        editing={editing}
        statusOptions={options.itemStatus}
        documents={documents}
        saving={itemSaving}
        error={itemError}
        onChange={(patch) =>
          setEditing((e) => (e ? { ...e, form: { ...e.form, ...patch } } : e))
        }
        onClose={() => (itemSaving ? null : setEditing(null))}
        onSave={saveItem}
      />

      {/* Row upload (button or dropped file) — straight to the upload panel;
          the new file is linked and the item marked done on upload. */}
      {rowUpload && (
        <CorrespondentUploadPanel
          cid={cid}
          defaultCategory={uploadCategoryOf(rowUpload.row.tpl)}
          initialFile={rowUpload.file || undefined}
          versions={documents}
          subtitle={`${detail?.companyName || ""} · ${rowUpload.row.tpl.label}`}
          onClose={() => setRowUpload(null)}
          onUploaded={(res) => onRowUploaded(rowUpload.row, res)}
        />
      )}

      {/* Status chip menu — one click, saved at once. */}
      <Menu
        anchorEl={statusMenu?.anchor || null}
        open={!!statusMenu}
        onClose={() => setStatusMenu(null)}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        {(options.itemStatus || []).map((s) => {
          const current = statusMenu?.row?.item?.status || "Pending";
          return (
            <MenuItem
              key={s}
              selected={s === current}
              sx={{ fontSize: 13 }}
              onClick={() => {
                const row = statusMenu.row;
                setStatusMenu(null);
                if (s !== current) quickSaveItem(row, { status: s });
              }}
            >
              {s}
              {s === statusMenu?.row?.tpl?.doneStatus && (
                <Box component="span" sx={{ ml: 1, fontSize: 11.5, color: PT.muted }}>
                  marks it done
                </Box>
              )}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Link an existing file — ONE menu of every document on the
          correspondent that this item doesn't already link (name, then its
          category underneath). Click one and it's linked. */}
      <Menu
        anchorEl={linkMenu?.anchor || null}
        open={!!linkMenu}
        onClose={() => setLinkMenu(null)}
        slotProps={{ paper: { sx: { minWidth: 280, maxWidth: 420, maxHeight: 360 } } }}
      >
        {linkMenu &&
          (() => {
            const linkable = documents.filter(
              (d) => !(linkMenu.row.item?.documentIds || []).includes(oidOf(d)),
            );
            if (!linkable.length)
              return (
                <MenuItem disabled sx={{ fontSize: 13 }}>
                  No other files on this correspondent
                </MenuItem>
              );
            return linkable.map((d) => (
              <MenuItem
                key={oidOf(d)}
                onClick={() => {
                  const row = linkMenu.row;
                  setLinkMenu(null);
                  quickSaveItem(row, {
                    documentIds: [...(row.item?.documentIds || []), oidOf(d)],
                  });
                }}
              >
                <Flx column sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: 13,
                      color: PT.ink,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.file_display_name || d.file_name_as_loaded || "Untitled"}
                  </Box>
                  {d.category && (
                    <Box sx={{ fontSize: 11.5, color: PT.muted }}>{d.category}</Box>
                  )}
                </Flx>
              </MenuItem>
            ));
          })()}
      </Menu>

      {uploading && editing && (
        <CorrespondentUploadPanel
          cid={cid}
          defaultCategory={
            ITEM_UPLOAD_CATEGORY[editing.tpl.key] ||
            ITEM_UPLOAD_CATEGORY[String(editing.tpl.id).split(".")[1]] ||
            "Other"
          }
          versions={documents}
          subtitle={`${detail?.companyName || ""} · ${editing.tpl.label}`}
          onClose={() => setUploading(false)}
          onUploaded={(res) => {
            const before = new Set(documents.map(oidOf));
            const next = (res?.documents ?? []).filter(Boolean);
            const added = next.map(oidOf).filter((id) => id && !before.has(id));
            if (next.length) setUploadedDocs(next);
            if (added.length)
              setEditing((e) =>
                e
                  ? {
                      ...e,
                      form: {
                        ...e.form,
                        documentIds: [
                          ...new Set([...(e.form.documentIds || []), ...added]),
                        ],
                      },
                    }
                  : e,
              );
            setUploading(false);
            // Refresh the record so the Agreements tab and the header see it.
            onChanged?.();
          }}
        />
      )}

      <AgreementDocumentModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        doc={previewDoc}
        companyId={cid}
        versions={documents.filter(
          (d) => previewDoc && d.category === previewDoc.category,
        )}
      />
    </>
  );
}

CorrespondentOnboardingTab.propTypes = {
  detail: PropTypes.object.isRequired,
  onProgress: PropTypes.func,
  onChanged: PropTypes.func,
  onOpenAgreements: PropTypes.func,
};
